import { useEffect, useMemo, useState } from "react";

import {
  configureProductionTracker,
  getProductionTracker,
  resetProductionTracker,
} from "@/features/devices/api";
import type { ProductionTracker } from "@/features/devices/types";
import { parseTag } from "@/features/machines/addressing";
import type { Machine } from "@/features/machines/types";
import { resolveProductionTotalMode } from "./productionSources";
import type { DashboardWidget } from "./types";

export type WidgetProductionTrackerState =
  | { status: "loading" }
  | { status: "ready"; data: ProductionTracker }
  | { status: "resetting"; data: ProductionTracker }
  | { status: "unavailable" }
  | { status: "error" };

interface TrackerRequest {
  key: string;
  workspaceId: string;
  deviceId: string;
  portIndex: number;
  target: number | null;
  unit: string | null;
  widgetIds: string[];
}

export function useProductionTrackers(
  machine: Machine,
  widgets: DashboardWidget[],
  enabled = true,
): {
  states: Map<string, WidgetProductionTrackerState>;
  reset: (widgetId: string) => Promise<void>;
} {
  const requests = useMemo(
    () => buildTrackerRequests(machine, widgets),
    [machine, widgets],
  );
  const signature = useMemo(
    () =>
      requests
        .map(
          (request) =>
            `${request.key}:${request.target}:${request.unit ?? ""}:${request.widgetIds.join(",")}`,
        )
        .join("|"),
    [requests],
  );
  const [states, setStates] = useState<
    Map<string, WidgetProductionTrackerState>
  >(() => new Map());
  // Estado interno pertence a workspace/device/porta, nao ao id reutilizavel
  // do widget. Cada retomada cria um token; reset tardio nao cruza esse limite.
  const activity = useMemo(() => ({ enabled, signature, active: false, revision: 0, resetting: false }), [enabled, signature]);
  const [stateActivity, setStateActivity] = useState<object | null>(null);

  useEffect(() => {
    if (!enabled) return;
    if (requests.length === 0) {
      setStates(new Map());
      return;
    }

    activity.active = true;
    let controller = new AbortController();
    let pending = false;
    let queued = false;

    const load = async (showLoading: boolean) => {
      if (!activity.active || activity.resetting) return;
      // Rede lenta nao pode ser abortada a cada 5s para nunca concluir.
      if (pending) { queued = true; return; }
      pending = true;
      controller = new AbortController();
      const requestController = controller;
      const revision = activity.revision;
      if (showLoading) {
        setStateActivity(activity);
        setStates((current) => {
          const next = new Map(current);
          for (const request of requests) {
            next.set(request.key, { status: "loading" });
          }
          return next;
        });
      }

      await Promise.all(
        requests.map(async (request) => {
          try {
            let tracker: ProductionTracker;
            try {
              tracker = await getProductionTracker(
                request.workspaceId,
                request.deviceId,
                request.portIndex,
                requestController.signal,
              );
            } catch (error) {
              if ((error as { status?: number }).status !== 404) throw error;
              if (!activity.active || requestController.signal.aborted || revision !== activity.revision) return;
              tracker = await configureProductionTracker(
                request.workspaceId,
                request.deviceId,
                request.portIndex,
                { target: request.target, unit: request.unit },
                requestController.signal,
              );
            }
            // O tracker no servidor e a fonte de verdade analitica. Divergencia
            // com layout local antigo nao pode disparar PUT no polling, pois
            // alternaria meta/baseline entre clientes e corromperia ciclos.
            if (!activity.active || requestController.signal.aborted || revision !== activity.revision) return;
            if (!matchesRequest(tracker, request)) throw new Error("Tracker de outra fonte.");
            setStates((current) => {
              const next = new Map(current);
              next.set(request.key, {
                status: "ready",
                data: tracker,
              });
              return next;
            });
          } catch (error) {
            if (!activity.active || requestController.signal.aborted || revision !== activity.revision || (error as { name?: string }).name === "AbortError") return;
            const nextState: WidgetProductionTrackerState =
              (error as { status?: number }).status === 503
                ? { status: "unavailable" }
                : { status: "error" };
            setStates((current) => {
              const next = new Map(current);
              next.set(request.key, nextState);
              return next;
            });
          }
        }),
      );
      pending = false;
      if (activity.active && queued) {
        queued = false;
        void load(false);
      }
    };

    void load(true);
    const interval = window.setInterval(() => void load(false), 5_000);
    return () => {
      activity.active = false;
      controller.abort();
      window.clearInterval(interval);
    };
    // `signature` serializa `requests` e é a chave de dedupe deliberada.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature, enabled, activity]);

  const reset = async (widgetId: string) => {
    const request = requests.find((candidate) =>
      candidate.widgetIds.includes(widgetId),
    );
    if (!request || !enabled || !activity.active || stateActivity !== activity || activity.resetting) return;
    const current = states.get(request.key);
    if (current?.status !== "ready") return;
    activity.resetting = true;
    const revision = ++activity.revision;

    setStates((previous) => {
      const next = new Map(previous);
      next.set(request.key, {
        status: "resetting",
        data: current.data,
      });
      return next;
    });
    try {
      const tracker = await resetProductionTracker(
        request.workspaceId,
        request.deviceId,
        request.portIndex,
      );
      if (!activity.active || revision !== activity.revision) return;
      if (!matchesRequest(tracker, request)) throw new Error("Tracker de outra fonte.");
      setStates((previous) => {
        const next = new Map(previous);
        next.set(request.key, {
          status: "ready",
          data: tracker,
        });
        return next;
      });
    } catch {
      if (!activity.active || revision !== activity.revision) return;
      setStates((previous) => {
        const next = new Map(previous);
        next.set(request.key, { status: "error" });
        return next;
      });
    } finally {
      activity.resetting = false;
    }
  };

  const byWidget = new Map<string, WidgetProductionTrackerState>();
  for (const request of requests) {
    const state = enabled && stateActivity === activity ? states.get(request.key) : undefined;
    for (const id of request.widgetIds) byWidget.set(id, state ?? { status: "loading" });
  }
  return { states: byWidget, reset };
}

export function buildTrackerRequests(
  machine: Machine,
  widgets: DashboardWidget[],
): TrackerRequest[] {
  const byKey = new Map<string, TrackerRequest>();

  for (const widget of widgets) {
    const production = widget.settings.production;
    const totalMode = resolveProductionTotalMode(production);
    if (
      widget.widgetType !== "production-kpi" ||
      production?.mode !== "total" ||
      (production.window && production.window !== "current") ||
      totalMode === "physical" ||
      (totalMode === "lot" && (!production.target || production.target <= 0))
    ) {
      continue;
    }
    const address = parseTag(widget.source);
    if (!address) continue;
    const deviceId = machine.slots.find(
      (slot) => slot.slotIndex === address.slotIndex,
    )?.deviceId;
    if (!deviceId) continue;
    const key = `${machine.workspaceId}:${deviceId}:${address.portIndex}`;
    const existing = byKey.get(key);
    if (existing) {
      existing.widgetIds.push(widget.id);
      continue;
    }
    byKey.set(key, {
      key,
      workspaceId: machine.workspaceId,
      deviceId,
      portIndex: address.portIndex,
      target: totalMode === "lot" ? production.target ?? null : null,
      unit: widget.settings.unit?.trim() || null,
      widgetIds: [widget.id],
    });
  }

  return [...byKey.values()];
}

function matchesRequest(tracker: ProductionTracker, request: TrackerRequest): boolean {
  return tracker.workspaceId === request.workspaceId && tracker.deviceId === request.deviceId &&
    tracker.portIndex === request.portIndex;
}
